/**
 * Pure assignee-name resolution for the record comments panel
 * (operator-task-record-comments-ui-slice-3-assign-to-identity).
 *
 * Kept as a pure function (mirroring commentRead.ts / commentScroll.ts) so the
 * resolution order is unit-tested independently of the redux/restClient-bound
 * panel component.
 *
 * WHY the per-comment relation is the PRIMARY source (grounded 2026-09-16):
 * the picker's `user_plain_list` is scoped to what the VIEWER can list (RBAC)
 * and is capped, so a USER assignee the viewer cannot enumerate (e.g. a
 * founder, from the Operator/a restricted session — which returns 0 USER rows)
 * would fall out of that list and the badge would show a bare "Identity <id>".
 * The server-side per-comment `assignee_identity_id_identity { name }` relation
 * resolves such names fine, exactly like the author relation, so it is tried
 * first; the client list and a short-id stub are fallbacks.
 */

/** The minimal comment shape needed to resolve an assignee label. */
export interface AssigneeResolvableComment {
    assignee_identity_id: string | null;
    assignee_identity_id_identity: { name: string } | null;
}

/**
 * Human label for a comment's assignee, in priority order:
 *   1. "" — the comment is unassigned.
 *   2. "you" — the assignee is the current viewer.
 *   3. the server-side per-comment relation name (works even when the viewer
 *      cannot list the identity via RBAC).
 *   4. a name from the client-fetched picker list (nameById fallback).
 *   5. "Identity <id8>" — unresolvable everywhere (RBAC / deleted / absent).
 */
export const resolveAssigneeName = (
    comment: AssigneeResolvableComment,
    currentIdentityId: string | undefined,
    nameById: ReadonlyMap<string, string>
): string => {
    const assigneeId = comment.assignee_identity_id;
    if (!assigneeId) {
        return "";
    }
    if (currentIdentityId && assigneeId === currentIdentityId) {
        return "you";
    }
    return (
        comment.assignee_identity_id_identity?.name ??
        nameById.get(assigneeId) ??
        `Identity ${assigneeId.slice(0, 8)}`
    );
};

/**
 * A USER row fetched to populate the comment-assignee picker. A USER's `id` IS
 * the identity id stored in `record_comment.assignee_identity_id` (a USER is an
 * identity subtype sharing its primary key), so the id is used directly as the
 * assignee.
 */
export interface AssigneeUserRow {
    id: string;
    name: string;
    deleted_at: string | null;
}

/** A single option for the assignee picker (react-select `{value,label}` shape). */
export interface AssigneeOption {
    value: string;
    label: string;
}

/**
 * The pickable assignee options: human USER records only (comments are directed
 * at a person to answer), excluding deleted users so a removed account cannot be
 * picked. Deliberately does NOT include non-USER identities (service-users,
 * roles/groups, webhooks, …) — confirmed by Margot (2026-09-23): a comment is
 * only ever assigned to a user.
 */
export const buildAssigneeOptions = (
    rows: readonly AssigneeUserRow[]
): AssigneeOption[] =>
    rows.filter((row) => !row.deleted_at).map((row) => ({ value: row.id, label: row.name }));

/**
 * Name lookup over ALL fetched users — including deleted ones — so a comment
 * still assigned to a since-deleted user resolves that user's name (as a
 * secondary fallback behind the per-comment relation) rather than a bare id,
 * even though the deleted user is not offered in the picker.
 */
export const buildAssigneeNameMap = (
    rows: readonly AssigneeUserRow[]
): Map<string, string> => {
    const map = new Map<string, string>();
    for (const row of rows) {
        map.set(row.id, row.name);
    }
    return map;
};

/** How an assignee chip is styled: open and assigned to the viewer, open and
 * assigned to someone else, or done. */
export type AssigneeBadgeVariant = "mine" | "other" | "done";

/**
 * Chip variant for an assigned comment. An open comment assigned to the viewer
 * is "mine" (highlighted so it stands out); a resolved one is "done" (muted)
 * whoever it is assigned to; anything else is "other" (neutral).
 */
export const assigneeBadgeVariant = (
    comment: { assignee_identity_id: string | null; resolved_at?: string | null },
    currentIdentityId: string | undefined
): AssigneeBadgeVariant => {
    if (comment.resolved_at) {
        return "done";
    }
    if (currentIdentityId && comment.assignee_identity_id === currentIdentityId) {
        return "mine";
    }
    return "other";
};

/**
 * Sentinel option value for "remove the assignment" in the header assignee
 * dropdown. Not a valid identity id, so it can never collide with a user.
 */
export const UNASSIGN_OPTION_VALUE = "__comment-unassign__";

/**
 * Options of the header assignee dropdown (Margot 2026-11-03: the Assign control
 * sits at the top of a posted comment; clicking the assignee chip opens the
 * picker under it). With an assignee set, a leading "Nobody" option removes the
 * assignment; without one, only the users are offered.
 */
export const buildHeaderAssigneeOptions = (
    userOptions: readonly AssigneeOption[],
    hasAssignee: boolean
): AssigneeOption[] =>
    hasAssignee
        ? [{ value: UNASSIGN_OPTION_VALUE, label: "Nobody (remove assignment)" }, ...userOptions]
        : [...userOptions];

/**
 * The assignee to write for a dropdown selection, or `undefined` when nothing
 * changes (picking the current assignee, or clearing an unassigned comment).
 * `null` clears the assignment.
 */
export const resolveAssigneeSelection = (
    selected: string | null | undefined,
    current: string | null
): string | null | undefined => {
    const next = !selected || selected === UNASSIGN_OPTION_VALUE ? null : selected;
    return next === current ? undefined : next;
};
